import { useMutation } from '@tanstack/react-query';
import { generateKeyBetween } from 'fractional-indexing';
import type { QuoteDTO } from '@setvi/shared/services/react-query';
import { moveQuoteV2ItemsMutation } from 'Services/Mutations/QuotesV2';
import {
  moveItemsToSectionOptimistically,
  getLastPositionInContainer
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface MoveItemsVariables {
  itemIds: string[];
  targetSectionId: string | null;
}

/**
 * Move multiple items to a section via V2 move endpoint.
 * Sends all moves in a single batch request.
 * Applies optimistic update immediately, then syncs with server.
 */
export const useMoveItems = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = moveQuoteV2ItemsMutation();

  return useMutation({
    mutationFn: ({ itemIds, targetSectionId }: MoveItemsVariables) => {
      const items = cache.getItems() || [];
      let lastPos = getLastPositionInContainer(items, targetSectionId);

      const moves = itemIds.map(itemId => {
        const position = generateKeyBetween(lastPos, null);
        lastPos = position;
        return {
          itemId,
          targetSectionId,
          position
        };
      });

      return mutationFn({ quoteId, moves });
    },

    onMutate: async ({ itemIds, targetSectionId }: MoveItemsVariables) => {
      await cache.cancel();

      const previousQuote = cache.getQuote();
      const previousItems = cache.getItems();

      const composed = previousQuote
        ? ({
            ...previousQuote,
            items: previousItems || []
          } as unknown as QuoteDTO)
        : undefined;

      const updated = moveItemsToSectionOptimistically(
        composed,
        itemIds,
        targetSectionId
      );

      if (updated) {
        const { items: updatedItems, ...quoteData } = updated as any;
        cache.setQuote(() => quoteData);
        cache.setItems(() => updatedItems);
      }

      return { previousQuote, previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: () => {
      cache.invalidate();
    }
  });
};
